import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MCUserAPI } from "../utils/MCUserAPI";
import { useAppLayerContext } from "../contexts/AppLayer";

export interface SavedPaymentMethod {
    cardType?: string;
    lastFour?: string;
}
interface UseMCUserProps {
    enabled?: boolean;
}

interface UseMCUserReturn {
    isLoading: boolean;
    petOwnerId: number | null;
    savedPaymentMethod: SavedPaymentMethod | undefined;
    isPaperless: boolean;
}

export const useMCUser = ({ enabled = true }: UseMCUserProps = {}): UseMCUserReturn => {
    const { appState, updateAppState } = useAppLayerContext();
    const { mcAuthToken } = appState;

    const { data, isLoading } = useQuery({
        queryKey: ["mcUser", mcAuthToken],
        queryFn: async () => {
            const mcUserApi = new MCUserAPI(mcAuthToken!);
            return mcUserApi.getUser();
        },
        staleTime: 1000 * 60 * 60,
        enabled: !!mcAuthToken && enabled
    });

    // Derive values from query data
    const petOwnerId = data?.petOwner?.petOwnerId ?? null;
    const isPaperless = !!data?.petOwner?.paperless;

    const creditCard = data?.billing?.creditCard;
    const savedPaymentMethod: SavedPaymentMethod = useMemo(
        () => ({ cardType: creditCard?.creditCardType, lastFour: creditCard?.creditCardNumber }),
        [creditCard?.creditCardType, creditCard?.creditCardNumber]
    );

    // Sync savedPaymentMethod and petOwnerId to appState for consumers that read from appState
    useEffect(() => {
        updateAppState({ savedPaymentMethod });
    }, [savedPaymentMethod, updateAppState]);

    useEffect(() => {
        if (petOwnerId !== null) {
            updateAppState({ mcPetOwnerId: petOwnerId });
        }
    }, [petOwnerId, updateAppState]);

    return { isLoading, petOwnerId, savedPaymentMethod, isPaperless };
};
